'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { usePersistentDebugLogger } from '@/hooks/telemetry';
import { SessionsDisplayMode } from '@/types/frontend-types';

interface UseSessionsModalStateOutput {
  displayMode: SessionsDisplayMode;
  isSearchInitiated: boolean;
  isSessionsModalOpen: boolean;
  onDisplayModeToggle: () => void;
  onSearchQueryChange: (query: string) => void;
  onSearchToggle: () => void;
  onSessionCountChange: (count: number | null) => void;
  onSessionsModalClose: () => void;
  openSessionsModal: () => void;
  searchQuery: string;
  sessionCount: number | null;
}

const NEXT_SESSIONS_DISPLAY_MODE: Record<SessionsDisplayMode, SessionsDisplayMode> = {
  [SessionsDisplayMode.Active]: SessionsDisplayMode.Archived,
  [SessionsDisplayMode.Archived]: SessionsDisplayMode.Active,
};
const SESSIONS_MODAL_STYLE_CONFLICT_INSTANCE_ID = 'ccontainer-style-conflict--2026-07-01';
const SESSIONS_MODAL_STYLE_CONFLICT_FEATURE = 'ccontainer-style-conflict';
const SESSIONS_MODAL_STATE_COMPONENT = 'SessionsModalState';
const SESSIONS_MODAL_OPEN_EVENT = 'sessions-modal-open-requested';
const SESSIONS_MODAL_CLOSE_EVENT = 'sessions-modal-close-requested';
const SESSIONS_MODAL_VISIBLE_EVENT = 'sessions-modal-visible-committed';
const SESSIONS_MODAL_SEARCH_TOGGLE_EVENT = 'sessions-modal-search-toggle-requested';
const SESSIONS_MODAL_DISPLAY_MODE_EVENT = 'sessions-modal-display-mode-toggle-requested';
const SESSIONS_MODAL_COUNT_EVENT = 'sessions-modal-count-changed';

export function useSessionsModalState(): UseSessionsModalStateOutput {
  const [isSessionsModalOpen, setIsSessionsModalOpen] = useState(false);
  const [sessionCount, setSessionCount] = useState<number | null>(null);
  const [isSearchInitiated, setIsSearchInitiated] = useState(false);
  const [displayMode, setDisplayMode] = useState<SessionsDisplayMode>(SessionsDisplayMode.Active);
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryLength = searchQuery.length;
  const logger = usePersistentDebugLogger({
    component: SESSIONS_MODAL_STATE_COMPONENT,
    instanceId: SESSIONS_MODAL_STYLE_CONFLICT_INSTANCE_ID,
    metadata: {
      feature: SESSIONS_MODAL_STYLE_CONFLICT_FEATURE,
    },
  });
  const previousIsSessionsModalOpenRef = useRef<boolean | null>(null);

  useLayoutEffect(() => {
    const previousIsSessionsModalOpen = previousIsSessionsModalOpenRef.current;
    previousIsSessionsModalOpenRef.current = isSessionsModalOpen;

    if (previousIsSessionsModalOpen === isSessionsModalOpen) {
      return;
    }

    logger.logStateTransition({
      name: SESSIONS_MODAL_VISIBLE_EVENT,
      from: previousIsSessionsModalOpen,
      to: isSessionsModalOpen,
      payload: {
        displayMode,
        isSearchInitiated,
        searchQueryLength,
        sessionCount,
      },
    });
  }, [displayMode, isSearchInitiated, isSessionsModalOpen, logger, searchQueryLength, sessionCount]);

  const onSessionsModalClose = useCallback((): void => {
    logger.logEventFlow({
      name: SESSIONS_MODAL_CLOSE_EVENT,
      payload: {
        displayMode,
        isSearchInitiated,
        searchQueryLength,
        sessionCount,
      },
    });
    setIsSearchInitiated(false);
    setDisplayMode(SessionsDisplayMode.Active);
    setSearchQuery('');
    setIsSessionsModalOpen(false);
  }, [displayMode, isSearchInitiated, logger, searchQueryLength, sessionCount]);

  const openSessionsModal = useCallback((): void => {
    logger.logEventFlow({
      name: SESSIONS_MODAL_OPEN_EVENT,
      payload: {
        displayMode,
        isSearchInitiated,
        searchQueryLength,
        sessionCount,
      },
    });
    setIsSessionsModalOpen(true);
  }, [displayMode, isSearchInitiated, logger, searchQueryLength, sessionCount]);

  const onSessionCountChange = useCallback((count: number | null): void => {
    logger.logStateTransition({
      name: SESSIONS_MODAL_COUNT_EVENT,
      from: sessionCount,
      to: count,
      payload: {
        displayMode,
        isSessionsModalOpen,
      },
    });
    setSessionCount(count);
  }, [displayMode, isSessionsModalOpen, logger, sessionCount]);

  const onDisplayModeToggle = useCallback((): void => {
    const nextDisplayMode = NEXT_SESSIONS_DISPLAY_MODE[displayMode];
    logger.logStateTransition({
      name: SESSIONS_MODAL_DISPLAY_MODE_EVENT,
      from: displayMode,
      to: nextDisplayMode,
      payload: {
        isSessionsModalOpen,
        sessionCount,
      },
    });
    setDisplayMode(nextDisplayMode);
  }, [displayMode, isSessionsModalOpen, logger, sessionCount]);

  const onSearchToggle = useCallback((): void => {
    const nextIsSearchInitiated = !isSearchInitiated;
    logger.logStateTransition({
      name: SESSIONS_MODAL_SEARCH_TOGGLE_EVENT,
      from: isSearchInitiated,
      to: nextIsSearchInitiated,
      payload: {
        displayMode,
        isSessionsModalOpen,
        previousSearchQueryLength: searchQueryLength,
        willClearSearchQuery: isSearchInitiated,
      },
    });

    if (isSearchInitiated) {
      setSearchQuery('');
    }

    setIsSearchInitiated(nextIsSearchInitiated);
  }, [displayMode, isSearchInitiated, isSessionsModalOpen, logger, searchQueryLength]);

  const onSearchQueryChange = useCallback((query: string): void => {
    setSearchQuery(query);
  }, []);

  return {
    displayMode,
    isSearchInitiated,
    isSessionsModalOpen,
    onDisplayModeToggle,
    onSearchQueryChange,
    onSearchToggle,
    onSessionCountChange,
    onSessionsModalClose,
    openSessionsModal,
    searchQuery,
    sessionCount,
  };
}
